/**
=========================================================
* Material Dashboard 2 PRO React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import Swal from "sweetalert2";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// NewEvent page components
import FormFields from "./components/FormFields";
import { addNewMediaImgsApi } from "api/medias";

import { useNavigate } from "react-router-dom";
import { getAllEventApi } from "api/events";
import { addNewReservationApi, sendNewReservationEmailApi } from "api/tickets";
import { CircularProgress } from "@mui/material";
import { getAllEventTitleApi } from "api/events";
import { verifyReservationId, addMissingReservationApi } from "api/tickets";
import DefaultReservationCards from "examples/Cards/ReservationCards/DefaultReservationCards";
const initialFormState = {
  formInfo: {
    reservationId: "",
  },
  seatInfo: [],
};

function VerifyReservation() {
  const navigate = useNavigate();

  useEffect(() => {
    getInitialData();
  }, []);

  const [eventData, setEventData] = useState([]);
  const [formValue, setFormValue] = useState(initialFormState);
  const [isLoading, setIsLoading] = useState(false);
  const [ticketInfo, setTicketInfo] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDates, setSelectedDates] = useState({});

  const getInitialData = async () => {
    try {
      setIsLoading(true);

      const eventResponse = await getAllEventTitleApi();
      const eventDataArray = eventResponse.map((el) => ({
        title: el.eventTitle,
        id: el._id,
        eventSiege: el.eventSiege,
        isSelectetivePlaces: el.isSelectetivePlaces,
        seatIoId: el.seatIoId,
        eventDateList: el.eventDateList,
        selectedTimeId: el.selectedTimeId,
        isCinema: el.isCinema,
      }));
      setEventData(eventDataArray);
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetFormValues = (value) => {
    try {
      setFormValue((prevFormValue) => ({
        ...prevFormValue,
        formInfo: value,
      }));
    } catch (err) {
      console.log(err);
    }
  };
  const handleGetSelectedPlaceEvent = (value) => {
    try {
      setFormValue((prevFormValue) => ({
        ...prevFormValue,
        seatInfo: value,
      }));
    } catch (err) {
      console.log(err);
    }
  };

  const handleVerifyTickets = async () => {
    try {
      setTicketInfo(null);
      setIsLoading(true);

      const reservationId = formValue?.formInfo?.reservationId || "L";
      const verifyReservationRes = await verifyReservationId(reservationId);

      if (verifyReservationRes.error) {
        return Swal.fire({
          title: "Commande introuvable",
          text: verifyReservationRes.error,
          icon: "error",
        });
      }

      const logData = verifyReservationRes.logData || null;
      setTicketInfo({
        logData,
        userInfo: verifyReservationRes.userInfo || null,
        isExist: verifyReservationRes.isExist,
      });
      // Initialize selectedDates from log data (item._id → selectedTimeId)
      const initDates = {};
      (logData?.data || []).forEach((item) => {
        if (item.selectedTimeId) initDates[item._id] = item.selectedTimeId;
      });
      setSelectedDates(initDates);
      setModalOpen(true);
    } catch (error) {
      console.error("Erreur générale:", error);
      Swal.fire({
        title: "Erreur",
        text: "Une erreur est survenue lors de la vérification.",
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddMissingReservation = async () => {
    try {
      setIsLoading(true);
      const reservationId = ticketInfo?.logData?.orderUniqueHash;
      const res = await addMissingReservationApi(reservationId, selectedDates);
      setModalOpen(false);
      setTicketInfo(null);
      Swal.fire({
        title: "Succès",
        text: res.message || "Réservation ajoutée avec succès.",
        icon: "success",
      });
    } catch (error) {
      Swal.fire({
        title: "Erreur",
        text: error?.response?.data?.error || "Une erreur est survenue.",
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <MDBox>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} lg={6}>
              <MDTypography variant="h4" fontWeight="medium">
                Vérifier une réservation
              </MDTypography>
              <MDBox mt={1} mb={2}>
                <MDTypography variant="body2" color="text"></MDTypography>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormFields
              formValues={handleGetFormValues}
              currentFormValues={formValue}
              actionButton={
                <Grid item xs={12} lg={6}>
                  <MDBox mb={2}>
                    {isLoading ? (
                      <MDBox>
                        <CircularProgress size={40} color="info" />
                      </MDBox>
                    ) : (
                      <>
                        <MDButton variant="gradient" color="info" onClick={handleVerifyTickets}>
                          Vérifier
                        </MDButton>
                      </>
                    )}
                  </MDBox>
                </Grid>
              }
            />
          </Grid>
        </Grid>
      </MDBox>

      {/* Tickets Modal */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDTypography variant="h5" fontWeight="medium">
            {ticketInfo?.isExist ? "Réservation existante" : "Commande trouvée dans les logs"}
          </MDTypography>
          {!ticketInfo?.isExist && (
            <MDTypography variant="caption" color="text">
              Hash: {ticketInfo?.logData?.orderUniqueHash}
            </MDTypography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {ticketInfo?.isExist ? (
            <MDBox display="flex" flexDirection="column" alignItems="center" py={3}>
              <MDTypography variant="h6" color="info" fontWeight="medium" textAlign="center">
                Cette réservation existe déjà dans la base de données.
              </MDTypography>
              <MDTypography variant="body2" color="text" textAlign="center" mt={1}>
                ID : {formValue?.formInfo?.reservationId}
              </MDTypography>
            </MDBox>
          ) : null}
          {!ticketInfo?.isExist && ticketInfo?.userInfo && (
            <MDBox mb={3} p={2} sx={{ backgroundColor: "grey.100", borderRadius: 2 }}>
              <MDTypography variant="button" fontWeight="bold" mb={1} display="block">
                Informations client
              </MDTypography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <MDTypography variant="caption" color="text">Nom complet</MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {ticketInfo.userInfo.fullName || "—"}
                  </MDTypography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <MDTypography variant="caption" color="text">Email</MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {ticketInfo.userInfo.email || "—"}
                  </MDTypography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <MDTypography variant="caption" color="text">Téléphone</MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {ticketInfo.userInfo.phone || "—"}
                  </MDTypography>
                </Grid>
              </Grid>
            </MDBox>
          )}
          {!ticketInfo?.isExist && <Divider />}
          {!ticketInfo?.isExist && (!ticketInfo?.logData?.data || ticketInfo.logData.data.length === 0) && (
            <MDTypography variant="body2" color="text">
              Aucun article trouvé dans cette commande.
            </MDTypography>
          )}
          {!ticketInfo?.isExist && ticketInfo?.logData?.data?.map((item, index) => (
              <MDBox key={item._id || index}>
                {index > 0 && <Divider sx={{ my: 2 }} />}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <MDTypography variant="caption" color="text">
                      Événement
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="medium">
                      {item.fromEvent?.eventTitle || "—"}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDTypography variant="caption" color="text">
                      Date de création
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="medium">
                      {item.createdAt ? new Date(item.createdAt).toLocaleString("fr-FR") : "—"}
                    </MDTypography>
                  </Grid>
                  {(() => {
                    const availableDates = (item.fromEvent?.eventDateList || []).filter(
                      (d) => !d.isSoldOut
                    );
                    if (availableDates.length === 0) return null;
                    const isCinema = item.isCinema;
                    const currentVal = selectedDates[item._id] || "";
                    return (
                      <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                          <InputLabel>
                            {isCinema ? "Date et heure de séance" : "Date de l'événement"}
                          </InputLabel>
                          <Select
                            value={currentVal}
                            label={isCinema ? "Date et heure de séance" : "Date de l'événement"}
                            onChange={(e) =>
                              setSelectedDates((prev) => ({
                                ...prev,
                                [item._id]: e.target.value,
                              }))
                            }
                          >
                            {availableDates.map((d) => (
                              <MenuItem key={d.generatedId} value={d.generatedId}>
                                {isCinema
                                  ? new Date(d.eventDate).toLocaleString("fr-FR", {
                                      dateStyle: "long",
                                      timeStyle: "short",
                                      timeZone: "UTC",
                                    })
                                  : new Date(d.eventDate).toLocaleDateString("fr-FR", {
                                      dateStyle: "long",
                                      timeZone: "UTC",
                                    })}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    );
                  })()}
                  {item.selectedEventData?.map((siege, si) => (
                    <Grid item xs={12} key={siege._id || si}>
                      <MDBox pl={1} borderLeft="3px solid #ccc">
                        <Grid container spacing={1}>
                          <Grid item xs={6} sm={4}>
                            <MDTypography variant="caption" color="text">Catégorie</MDTypography>
                            <MDTypography variant="body2" fontWeight="medium">
                              {siege.category?.name || "—"}
                            </MDTypography>
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <MDTypography variant="caption" color="text">Quantité</MDTypography>
                            <MDTypography variant="body2" fontWeight="medium">
                              {siege.numberOfTickets ?? "—"}
                            </MDTypography>
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <MDTypography variant="caption" color="text">Prix</MDTypography>
                            <MDTypography variant="body2" fontWeight="medium">
                              {siege.price != null ? `${siege.price} MAD` : "—"}
                            </MDTypography>
                          </Grid>
                        </Grid>
                      </MDBox>
                    </Grid>
                  ))}
                </Grid>
              </MDBox>
            ))}
        </DialogContent>
        <DialogActions>
          {!ticketInfo?.isExist && (
            <MDButton variant="gradient" color="info" onClick={handleAddMissingReservation}>
              Ajouter cette résérvation
            </MDButton>
          )}
          <MDButton variant="outlined" color="secondary" onClick={() => setModalOpen(false)}>
            Fermer
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default VerifyReservation;
